import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { requestOutreachDraft, type OutreachDraft } from "./api";

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

export function OutreachDraftControl({
  signalId,
  contactId,
  buttonClassName,
  disabled = false,
}: {
  signalId: string;
  contactId?: string;
  buttonClassName: string;
  disabled?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [copied, setCopied] = useState(false);

  const draft = useMutation({
    mutationFn: () => requestOutreachDraft({ signalId, contactId }),
    onSuccess: (result: OutreachDraft) => {
      setSubject(result.subject);
      setBody(result.body);
      setCopied(false);
    },
  });

  const toggle = () => {
    if (disabled) return;
    if (!open && !draft.data && !draft.isPending) draft.mutate();
    setOpen((value) => !value);
  };

  const regenerate = () => {
    setCopied(false);
    draft.mutate();
  };

  const copyDraft = async () => {
    await copyText(`Subject: ${subject}\n\n${body}`);
    setCopied(true);
  };

  return (
    <div className="bdo-control">
      <button
        type="button"
        className={buttonClassName}
        disabled={disabled || draft.isPending}
        aria-expanded={open}
        onClick={toggle}
      >
        {draft.isPending ? "Drafting…" : "Draft Outreach"}
      </button>

      {open ? (
        <div className="bdo-panel" aria-live="polite">
          <div className="bdo-panel-head">
            <div>
              <p className="bdo-title">Outreach draft</p>
              <p className="bdo-review">Review and edit before sending.</p>
            </div>
            <button
              type="button"
              className="bdo-close"
              aria-label="Close outreach draft"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </div>

          {draft.isPending ? (
            <p className="bdo-status" role="status">
              Building a concise draft from this signal’s context…
            </p>
          ) : draft.isError ? (
            <div className="bdo-error" role="alert">
              <p>{draft.error.message || "The draft could not be generated."}</p>
              <button type="button" className="sx-btn sx-btn-p2" onClick={regenerate}>
                Try again
              </button>
            </div>
          ) : draft.data ? (
            <>
              <label className="bdo-label">
                Subject
                <input
                  className="bdo-subject"
                  value={subject}
                  maxLength={140}
                  onChange={(event) => {
                    setSubject(event.target.value);
                    setCopied(false);
                  }}
                />
              </label>
              <label className="bdo-label">
                Message
                <textarea
                  className="bdo-body"
                  value={body}
                  rows={9}
                  onChange={(event) => {
                    setBody(event.target.value);
                    setCopied(false);
                  }}
                />
              </label>
              <div className="bdo-actions">
                <button
                  type="button"
                  className="sx-btn sx-btn-hero"
                  disabled={!subject.trim() || !body.trim()}
                  onClick={() => void copyDraft()}
                >
                  {copied ? "Copied" : "Copy email"}
                </button>
                <button type="button" className="sx-btn sx-btn-p2" onClick={regenerate}>
                  Regenerate
                </button>
              </div>
              <p className="bdo-disclosure">
                BDSE created this draft for human review. It has not been sent.
              </p>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
